import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { FactorStatusValue } from 'constantsProject';
import type { PriceListRatioCoefficientRowData, ProjectData } from 'gql/graphql';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { formatNumberToRu } from 'utils';
import { getYesNoStatus } from 'utils/getYesNoTableStatus';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { FactorsMenuCellRendererComponent } from './cellRenderers';
import {
    priceListRatioCodeFilter,
    priceListRatioStatusFilter,
    priceListRatioWorkConditionsFilter,
} from './factorsQueries';

const MenuCellStyle = { padding: 0 };

interface IUsePriceListColumns {
    contractId: ProjectData['contractId'];
}

export const usePriceListColumns = ({ contractId }: IUsePriceListColumns) => {
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const variables = useMemo(() => ({ contractId }), [contractId]);

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [],
    );

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    'factorsList',
                    getDefaultFilterCleaner({
                        changeFilterHandler,
                        setFiltersWhere: setFilterValues,
                    }),
                ),
            }),
            [changeFilterHandler],
        ),
    );

    const columns = useMemo<
        (ColDef<PriceListRatioCoefficientRowData> | ColGroupDef<PriceListRatioCoefficientRowData>)[]
    >(
        () => [
            {
                field: 'menu' as any,
                headerName: '',
                width: 80,
                spanHeaderHeight: true,
                pinned: 'left',
                lockPosition: 'left',
                resizable: false,
                cellRenderer: FactorsMenuCellRendererComponent,
                cellClass: 'center-aligned-cell',
                cellStyle: MenuCellStyle,
            },
            {
                field: 'code',
                headerName: 'Шифр',
                width: 150,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListRatioCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                            width: 400,
                        },
                    ],
                    query: priceListRatioCodeFilter,
                },
            },
            {
                field: 'workConditions',
                headerName: 'Наименование',
                minWidth: 400,
                flex: 1,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables,
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListRatioWorkConditionsFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                            width: 400,
                        },
                    ],
                    query: priceListRatioWorkConditionsFilter,
                },
            },
            {
                headerName: 'Коэффициенты',
                marryChildren: true,
                children: [
                    {
                        field: 'kozp',
                        headerName: 'К озп',
                        width: 75,
                        valueFormatter: ({ value }) => formatNumberToRu(value, 2),
                    },
                    {
                        field: 'kem',
                        headerName: 'К эмм',
                        width: 78,
                        valueFormatter: ({ value }) => formatNumberToRu(value, 2),
                    },
                    {
                        field: 'knr',
                        headerName: 'К нр',
                        width: 70,
                        valueFormatter: ({ value }) => formatNumberToRu(value, 2),
                    },
                    {
                        field: 'ksp',
                        headerName: 'К сп',
                        width: 70,
                        valueFormatter: ({ value }) => formatNumberToRu(value, 2),
                    },
                    {
                        field: 'kmat',
                        headerName: 'К мат',
                        width: 75,
                        valueFormatter: ({ value }) => formatNumberToRu(value, 2),
                    },
                    {
                        field: 'ktz',
                        headerName: 'К тз',
                        width: 75,
                        valueFormatter: ({ value }) => formatNumberToRu(value, 2),
                    },
                    {
                        field: 'ktzzpm',
                        headerName: 'К тз зпм',
                        width: 105,
                        valueFormatter: ({ value }) => formatNumberToRu(value, 2),
                    },
                ],
            },
            {
                field: 'status',
                headerName: 'Статус записи',
                minWidth: 145,
                maxWidth: 250,
                flex: 1,
                spanHeaderHeight: true,
                valueFormatter: ({ value }) => FactorStatusValue[value] ?? '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    withSearch: false,
                    dataAccessor: 'uerPriceListRatioStatusFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус записи',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => FactorStatusValue[value],
                            width: 400,
                        },
                    ],
                    query: priceListRatioStatusFilter,
                },
            },
            {
                headerName: 'Используется',
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 105,
                        valueFormatter: ({ value }) => getYesNoStatus(value),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInAct',
                        headerName: 'Акт',
                        width: 105,
                        valueFormatter: ({ value }) => getYesNoStatus(value),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            hideTableHeader: true,
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        headerClass: 'maxWidthHeaderColumn',
                        width: 105,
                        spanHeaderHeight: true,
                        valueFormatter: ({ value }) => getYesNoStatus(value),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            hideTableHeader: true,
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
            {
                field: 'justification',
                headerName: 'Обоснование',
                minWidth: 150,
                flex: 1,
                spanHeaderHeight: true,
                valueFormatter: ({ value }) => value || '',
            },
        ],
        [changeFilterHandler, variables],
    );

    return useMemo(
        () => ({
            columns,
            filterValues,
        }),
        [columns, filterValues],
    );
};
